export const MAKEUPS_EXPIRY_IN_DAYS_KEY = "MAKEUPS_EXPIRY_IN_DAYS";
export const MAKEUPS_SETTINGS_GROUP = "MAKEUPS";

export const DEFAULT_MAKEUPS_EXPIRY_IN_DAYS = 30;

/** Stored value meaning makeup requests do not expire. */
export const MAKEUPS_NO_EXPIRY_DAYS = 99_999;

/** @deprecated Use DEFAULT_MAKEUPS_EXPIRY_IN_DAYS from settings. */
export const MAKEUP_ELIGIBILITY_LOOKBACK_DAYS = DEFAULT_MAKEUPS_EXPIRY_IN_DAYS;

const MIN_MAKEUPS_EXPIRY_IN_DAYS = 1;
const MAX_MAKEUPS_EXPIRY_IN_DAYS = MAKEUPS_NO_EXPIRY_DAYS;

export function isValidDraftMakeupsExpiryInDays(raw: string): boolean {
  const trimmed = String(raw).trim();
  if (trimmed === "") return false;

  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed)) return false;

  return (
    parsed >= MIN_MAKEUPS_EXPIRY_IN_DAYS && parsed < MAX_MAKEUPS_EXPIRY_IN_DAYS
  );
}

export function isMakeupsNoExpiry(expiryDays: number): boolean {
  return expiryDays >= MAKEUPS_NO_EXPIRY_DAYS;
}

export function parseMakeupsExpiryInDays(
  raw: string | number | null | undefined,
): number {
  if (raw == null) return DEFAULT_MAKEUPS_EXPIRY_IN_DAYS;

  const parsed =
    typeof raw === "number" ? raw : Number.parseInt(String(raw).trim(), 10);

  if (!Number.isFinite(parsed)) return DEFAULT_MAKEUPS_EXPIRY_IN_DAYS;
  if (parsed < MIN_MAKEUPS_EXPIRY_IN_DAYS) return MIN_MAKEUPS_EXPIRY_IN_DAYS;
  if (parsed > MAX_MAKEUPS_EXPIRY_IN_DAYS) return MAX_MAKEUPS_EXPIRY_IN_DAYS;
  return parsed;
}

export function formatMakeupsExpiryDurationPhrase(expiryDays: number): string {
  if (isMakeupsNoExpiry(expiryDays)) {
    return "no expiration limit";
  }
  return `${expiryDays} day${expiryDays === 1 ? "" : "s"}`;
}

export function formatMakeupsExpiryMemberWindowPhrase(expiryDays: number): string {
  if (isMakeupsNoExpiry(expiryDays)) {
    return "without a time limit";
  }
  return `for ${formatMakeupsExpiryDurationPhrase(expiryDays)}`;
}

export function formatMakeupsExpiryHowItWorksLine(expiryDays: number): string {
  if (isMakeupsNoExpiry(expiryDays)) {
    return "When a teacher marks your student absent, the missed class appears here with no expiration limit.";
  }
  return `When a teacher marks your student absent, the missed class appears here for ${expiryDays} day${expiryDays === 1 ? "" : "s"}.`;
}

export function formatMakeupsExpiryEligiblePanelDescription(
  expiryDays: number,
): string {
  if (isMakeupsNoExpiry(expiryDays)) {
    return "Request a makeup for sessions marked absent - there is no expiration limit.";
  }
  return `Request a makeup for sessions marked absent in the last ${expiryDays} day${expiryDays === 1 ? "" : "s"}.`;
}

export function formatMakeupsExpiryEmptyStateMessage(expiryDays: number): string {
  if (isMakeupsNoExpiry(expiryDays)) {
    return "When a teacher marks your student absent, eligible sessions appear here without a time limit. You can also track requests after you submit them.";
  }
  return `When a teacher marks your student absent, eligible sessions appear here for ${expiryDays} day${expiryDays === 1 ? "" : "s"}. You can also track requests after you submit them.`;
}

export function formatMakeupsExpiryDaysRemainingLabel(
  daysRemaining: number | null,
): string | null {
  if (daysRemaining == null) return null;
  if (daysRemaining === 0) return "Last day to request";
  return `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left to request`;
}
